import React, { useState } from 'react';
import { Table } from '../../Component/Table';
import { useTranslation } from 'react-i18next';
import { Button, Space, Popconfirm, message } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import Api from '../../Network';
import PaymentMethodModal from './PaymentMethodModal';
import NameByLang from '../../Component/nameByLang';
import { usePermissions } from '../../contexts/PermissionContext';
import { AnimatedContainer } from '../../Component/ui';
import './style.scss';

const PaymentMethods = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermissions();
  const queryClient = useQueryClient();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<any>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');

  // Delete payment method mutation
  const deletePaymentMethodMutation = useMutation({
    mutationFn: (paymentMethodId: number) =>
      Api.delete(`/api/admin/payment-methods/${paymentMethodId}`),
    onSuccess: () => {
      message.success(t('paymentMethods.deleteSuccess', 'Payment method deleted successfully'));
      queryClient.invalidateQueries({ queryKey: ['/api/admin/payment-methods'] });
    },
    onError: (_error: any) => {
      message.error(t('common.error', 'Error deleting payment method'));
    },
  });

  const handleDelete = (paymentMethodId: number) => {
    deletePaymentMethodMutation.mutate(paymentMethodId);
  };

  const handleEdit = (record: any) => {
    setSelectedPaymentMethod(record);
    setModalMode('edit');
    setModalVisible(true);
  };

  const handleAddNew = () => {
    setSelectedPaymentMethod(null);
    setModalMode('create');
    setModalVisible(true);
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSelectedPaymentMethod(null);
  };

  const handleModalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/admin/payment-methods'] });
  };

  return (
    <AnimatedContainer className="payment-methods-page" variant="fadeUp">
      <Table
        tableQueryKey="/api/admin/payment-methods"
        tableTitle={t('paymentMethods.title', 'Payment Methods')}
        showSearch={true}
        hideSearch={true}
        showDeleteSwitch={false}
        hideFilters={true}
        rowKey="id"
        createPermission="payment_methods.create"
        tableFilter={{
          filters: {},
          paging: { page: 1, pageSize: 20 },
          sorting: {},
        }}
        onTableFilterChange={(_path: string, _value: any) => {
          // Handle table filter changes if needed
        }}
        columns={[
          {
            title: t('paymentMethods.columns.nameEn', 'Name (EN)'),
            dataIndex: 'name_en',
            key: 'name_en',
            render: (name: string) => <NameByLang name={name} className="bold primary" />,
          },
          {
            title: t('paymentMethods.columns.nameAr', 'Name (AR)'),
            dataIndex: 'name_ar',
            key: 'name_ar',
            render: (name: string) => <NameByLang name={name} className="bold primary" />,
          },
          {
            title: t('paymentMethods.columns.name', 'Name'),
            key: 'name',
            render: (_: any, record: any) => (
              <NameByLang
                name_en={record.name_en}
                name_ar={record.name_ar}
                className="bold primary"
              />
            ),
          },
          {
            title: t('common.actions', 'Actions'),
            key: 'actions',
            render: (_: any, record: any) => (
              <Space>
                {(hasPermission('update_payment_method') ||
                  hasPermission('payment_methods.update') ||
                  hasPermission('edit_payment_method')) && (
                  <Button
                    type="link"
                    icon={<EditOutlined />}
                    onClick={() => handleEdit(record)}
                    size="small"
                  >
                    {t('common.edit', 'Edit')}
                  </Button>
                )}
                {(hasPermission('delete_payment_method') ||
                  hasPermission('payment_methods.delete')) && (
                  <Popconfirm
                    title={t(
                      'paymentMethods.deleteConfirm',
                      'Are you sure you want to delete this payment method?'
                    )}
                    description={t('paymentMethods.deleteWarning', 'This action cannot be undone.')}
                    onConfirm={() => handleDelete(record.id)}
                    okText={t('common.yes', 'Yes')}
                    cancelText={t('common.no', 'No')}
                  >
                    <Button type="link" danger icon={<DeleteOutlined />} size="small">
                      {t('common.delete', 'Delete')}
                    </Button>
                  </Popconfirm>
                )}
              </Space>
            ),
          },
        ]}
        buttonContent={
          hasPermission('create_payment_method') || hasPermission('payment_methods.create')
            ? t('paymentMethods.addNew', 'Add New Payment Method')
            : undefined
        }
        onClickNewButton={
          hasPermission('create_payment_method') || hasPermission('payment_methods.create')
            ? handleAddNew
            : undefined
        }
      />

      <PaymentMethodModal
        visible={modalVisible}
        mode={modalMode}
        paymentMethod={selectedPaymentMethod}
        onClose={handleModalClose}
        onSuccess={handleModalSuccess}
      />
    </AnimatedContainer>
  );
};

export default PaymentMethods;
